import { useState, useEffect, useRef } from 'react';
import {
  Typography, 
  Card,
  Space,
  Button, 
  Table, 
  Input,
  message,
  Spin,
  Tooltip,
  Modal,
  Form,
  Popconfirm,
  Select
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  SearchOutlined, 
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined
} from '@ant-design/icons';
import { supabase } from '../../../lib/supabase';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;

interface Leverancier {
  id: string;
  naam: string;
  contactpersoon: string;
  email: string;
  telefoon: string;
  adres: string;
  postcode: string;
  stad: string;
  land: string;
  created_at: string;
  updated_at: string;
}

// Create a separate component for the form to better manage state
function LeverancierForm({ 
  initialData, 
  onCancel, 
  onSubmit, 
  isVisible,
  key  // This forces a re-render when switching suppliers
}: { 
  initialData: Leverancier | null, 
  onCancel: () => void, 
  onSubmit: (values: any) => void, 
  isVisible: boolean,
  key: string
}) {
  const [form] = Form.useForm();

  // Initialize form when component mounts or initialData changes
  useEffect(() => {
    if (initialData) {
      // Fill form with supplier data
      form.setFieldsValue({
        naam: initialData.naam,
        contactpersoon: initialData.contactpersoon,
        email: initialData.email,
        telefoon: initialData.telefoon,
        adres: initialData.adres,
        postcode: initialData.postcode || '',
        stad: initialData.stad || '',
        land: initialData.land || 'Nederland'
      });
    } else {
      // Clear form for new supplier
      form.resetFields();
      
      // Set defaults
      form.setFieldsValue({
        land: 'Nederland'
      });
    }
  }, [initialData, form]);
  
  const handleSubmitClick = () => {
    // Validate form fields
    form.validateFields()
      .then(formValues => {
        // Check required fields
        if (!formValues.naam) {
          message.error('Naam is verplicht');
          return;
        }
        
        if (!formValues.adres) {
          message.error('Adres is verplicht');
          return;
        }
        
        console.log('Submitting values:', formValues);
        onSubmit(formValues);
      })
      .catch(errorInfo => {
        console.log('Validate Failed:', errorInfo);
      });
  };

  return (
    <Modal
      title={initialData ? 'Leverancier Bewerken' : 'Leverancier Toevoegen'}
      open={isVisible}
      onCancel={onCancel}
      destroyOnClose={true}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Annuleren
        </Button>,
        <Button 
          key="submit" 
          type="primary" 
          onClick={handleSubmitClick}
        >
          {initialData ? 'Bijwerken' : 'Aanmaken'}
        </Button>
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        preserve={false}  // Don't preserve form values between renders
      >
        <Form.Item
          name="naam"
          label="Naam"
          rules={[{ required: true, message: 'Voer een leveranciersnaam in' }]}
        >
          <Input prefix={<UserOutlined />} placeholder="Bedrijfsnaam" />
        </Form.Item>
        
        <Form.Item
          name="contactpersoon"
          label="Contactpersoon"
        >
          <Input prefix={<UserOutlined />} placeholder="Naam contactpersoon" />
        </Form.Item>
        
        <Form.Item
          name="email"
          label="Email"
          rules={[
            { type: 'email', message: 'Voer een geldig emailadres in' }
          ]}
        >
          <Input prefix={<MailOutlined />} placeholder="Emailadres" />
        </Form.Item>
        
        <Form.Item
          name="telefoon"
          label="Telefoon"
        >
          <Input prefix={<PhoneOutlined />} placeholder="Telefoonnummer" />
        </Form.Item>
        
        <Title level={5} style={{ marginTop: '16px', marginBottom: '16px' }}>Adresgegevens</Title>
        
        <Form.Item
          name="adres"
          label="Adres"
          rules={[{ required: true, message: 'Voer een adres in' }]}
        >
          <Input 
            prefix={<EnvironmentOutlined />} 
            placeholder="Straatnaam en huisnummer"
          />
        </Form.Item>
        
        <Space style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
          <Form.Item
            name="postcode"
            label="Postcode"
            style={{ marginBottom: 0, flex: 1 }}
          >
            <Input placeholder="Postcode" />
          </Form.Item>

          <Form.Item
            name="stad"
            label="Stad"
            style={{ marginBottom: 0, flex: 1 }}
          >
            <Input placeholder="Stad" />
          </Form.Item>
        </Space>
        
        <Form.Item
          name="land"
          label="Land"
          rules={[{ required: true, message: 'Voer een land in' }]}
        >
          <Input placeholder="Land" defaultValue="Nederland" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default function LeveranciersAdmin() {
  const [leveranciers, setLeveranciers] = useState<Leverancier[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingLeverancier, setEditingLeverancier] = useState<Leverancier | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const navigate = useNavigate();
  
  // Generate a unique key for the form to force re-rendering
  const [formKey, setFormKey] = useState('initial');

  // Check if the screen is mobile size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchLeveranciers();
  }, []);

  async function fetchLeveranciers() {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('leveranciers')
        .select('*')
        .order('naam');
      
      if (error) throw error;
      
      setLeveranciers(data || []);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      message.error('Error fetching suppliers');
    } finally {
      setLoading(false);
    }
  }

  const filteredLeveranciers = leveranciers.filter(leverancier => 
    leverancier.naam.toLowerCase().includes(searchQuery.toLowerCase()) ||
    leverancier.contactpersoon.toLowerCase().includes(searchQuery.toLowerCase()) ||
    leverancier.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const showModal = (leverancier?: Leverancier) => {
    if (leverancier) {
      setEditingLeverancier(leverancier);
    } else {
      setEditingLeverancier(null);
    }
    
    // Update the form key to force complete re-rendering
    setFormKey(`form-${Date.now()}`);
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleSubmit = async (values: any) => {
    try {
      console.log('Submitting values:', values);

      if (editingLeverancier) {
        // Update
        const { error } = await supabase
          .from('leveranciers')
          .update({
            ...values,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingLeverancier.id);
        
        if (error) throw error;
        message.success('Leverancier succesvol bijgewerkt');
      } else {
        // Create
        const { error } = await supabase
          .from('leveranciers')
          .insert([values]);
        
        if (error) throw error;
        message.success('Leverancier succesvol toegevoegd');
      }
      
      setIsModalVisible(false);
      fetchLeveranciers();
    } catch (error: any) {
      console.error('Error saving supplier:', error);
      message.error('Fout bij het opslaan van leverancier: ' + error.message);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      // Check if leverancier is linked to products
      const { count, error: countError } = await supabase
        .from('producten')
        .select('*', { count: 'exact', head: true })
        .eq('leverancier_id', id);
      
      if (countError) throw countError;
      
      if (count && count > 0) {
        message.error(`Cannot delete supplier with ${count} linked products`);
        return;
      }
      
      const { error } = await supabase
        .from('leveranciers')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      message.success('Supplier deleted successfully');
      fetchLeveranciers();
    } catch (error: any) {
      console.error('Error deleting supplier:', error);
      message.error('Error deleting supplier: ' + error.message);
    }
  };

  const getFullAddress = (record: Leverancier) => {
    const parts = [record.adres, record.postcode, record.stad, record.land].filter(Boolean);
    return parts.join(', ');
  };

  const columns: ColumnsType<Leverancier> = [
    {
      title: 'Naam',
      dataIndex: 'naam',
      key: 'naam',
      sorter: (a, b) => a.naam.localeCompare(b.naam),
    },
    {
      title: 'Contactpersoon',
      dataIndex: 'contactpersoon',
      key: 'contactpersoon',
      responsive: ['sm'],
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (email) => (
        <a href={`mailto:${email}`}>
          <Tooltip title={email}>
            <span>{email}</span>
          </Tooltip>
        </a>
      ),
      responsive: ['md'],
    },
    {
      title: 'Telefoon',
      dataIndex: 'telefoon',
      key: 'telefoon',
      responsive: ['lg'],
    },
    {
      title: 'Adres',
      key: 'adres',
      render: (_, record) => (
        <Tooltip title={getFullAddress(record)}>
          <span>{getFullAddress(record)}</span>
        </Tooltip>
      ),
      responsive: ['xl'],
    },
    {
      title: 'Aangemaakt op',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => dayjs(date).format('DD-MM-YYYY'),
      responsive: ['xl'],
    },
    {
      title: 'Acties',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            type="link"
            icon={<EditOutlined />} 
            onClick={() => showModal(record)}
            size={isMobile ? 'small' : 'middle'}
            title="Bewerk leverancier"
          />
          <Popconfirm
            title="Deze leverancier verwijderen?"
            description="Weet je zeker dat je deze leverancier wilt verwijderen?"
            onConfirm={() => handleDelete(record.id)}
            okText="Ja"
            cancelText="Nee"
          >
            <Button 
              type="link"
              danger
              icon={<DeleteOutlined />}
              size={isMobile ? 'small' : 'middle'}
              title="Verwijder leverancier"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card
        style={{
          borderRadius: '8px',
        }}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div style={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between', 
            alignItems: isMobile ? 'flex-start' : 'center',
            gap: isMobile ? '16px' : '0'
          }}>
            <Title level={4} style={{ margin: 0 }}>Leveranciersbeheer</Title>
            <Space 
              size="middle" 
              wrap={isMobile}
              style={{ 
                width: isMobile ? '100%' : 'auto',
                display: 'flex',
                flexWrap: 'wrap'
              }}
            >
              <Input
                placeholder="Zoek leveranciers..."
                prefix={<SearchOutlined />}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ 
                  width: isMobile ? '100%' : 200 
                }}
              />
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={() => showModal()}
              >
                Nieuwe Leverancier
              </Button>
            </Space>
          </div>

          <Table
            columns={columns}
            dataSource={filteredLeveranciers}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: isMobile ? false : true,
              showTotal: (total, range) => `${range[0]}-${range[1]} van ${total} leveranciers`,
            }}
            scroll={{ x: 'max-content' }}
            size={isMobile ? 'small' : 'middle'}
          />
        </Space>
      </Card>

      {/* Use the separate form component with a key to force complete re-rendering */}
      <LeverancierForm
        key={formKey}
        initialData={editingLeverancier}
        onCancel={handleCancel}
        onSubmit={handleSubmit}
        isVisible={isModalVisible}
      />
    </div>
  );
} 